import React, { useState, useEffect } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { Calendar } from "lucide-react";

interface Props {
  data?: string;
  setData?: (data: string) => void;
  label?: string;
}

function ModalData({ data = "", setData, label = "تاریخ:" }: Props) {
  const [dateValue, setDateValue] = useState<DateObject | null>(null);
  const [displayValue, setDisplayValue] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState(false);

  const toEnglishDigits = (str: string): string =>
    str.replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d).toString());

  // تابع برای دریافت تاریخ امروز به صورت شمسی
  const getTodayPersianDate = (): DateObject => {
    return new DateObject({ calendar: persian });
  };

  const handleDateChange = (date: DateObject | null) => {
    if (date) {
      const formattedDate = toEnglishDigits(date.format("YYYY/MM/DD").trim());

      setDateValue(date);
      setDisplayValue(formattedDate);
      setData?.(formattedDate);
    } else {
      setDateValue(null);
      setDisplayValue("");
      setData?.("");
    }
  };

  // مقداردهی اولیه فقط یک بار اجرا شود
  useEffect(() => {
    if (!isInitialized) {
      if (data && data.trim() !== "") {
        // اگر data از بیرون مقدار دارد، از آن استفاده کن
        try {
          const dateObj = new DateObject({
            date: data,
            calendar: persian,
            format: "YYYY/MM/DD",
          });
          setDateValue(dateObj);
          setDisplayValue(data);
        } catch (error) {
          console.error("Invalid date format:", data);
          // در صورت خطا، تاریخ امروز را تنظیم کن
          const today = getTodayPersianDate();
          const formattedDate = toEnglishDigits(
            today.format("YYYY/MM/DD").trim()
          );
          setDateValue(today);
          setDisplayValue(formattedDate);
          setData?.(formattedDate);
        }
      } else {
        // اگر data خالی است، تاریخ امروز را تنظیم کن
        const today = getTodayPersianDate();
        const formattedDate = toEnglishDigits(
          today.format("YYYY/MM/DD").trim()
        );
        setDateValue(today);
        setDisplayValue(formattedDate);
        setData?.(formattedDate);
      }

      setIsInitialized(true);
    }
  }, [isInitialized, data, setData]);

  // وقتی data از بیرون تغییر کرد و با مقدار داخلی متفاوت بود، displayValue را به روز کن
  useEffect(() => {
    if (isInitialized && data && data.trim() !== "" && data !== displayValue) {
      setDisplayValue(data);
      try {
        const dateObj = new DateObject({
          date: data,
          calendar: persian,
          format: "YYYY/MM/DD",
        });
        setDateValue(dateObj);
      } catch (error) {
        console.error("Invalid date format:", data);
        // در صورت خطا، مقدار قبلی را حفظ کن
      }
    }
  }, [data, isInitialized, displayValue]);

  return (
    <div className="flex-col w-full">
      <label className="inline-block w-full dark:text-white  mb-2 text-base font-medium">
        {label}
      </label>
      <div className="flex justify-center ml-3 text-center border border-gray-300 rounded-md  px-0 py-2 hover:border-blue-400 transition-all duration-200 ">
        <DatePicker
          calendar={persian}
          locale={persian_fa}
          value={dateValue}
          onChange={handleDateChange}
          format="YYYY/MM/DD"
          calendarPosition="bottom-center"
          editable={false}
          portal
          portalTarget={typeof document !== "undefined" ? document.body : undefined}
          zIndex={9999}
          mobileLabels={{
            OK: "تایید",
            CANCEL: "انصراف",
          }}
          render={(value: string | undefined, openCalendar: () => void) => (
            <input
              onClick={openCalendar}
              value={displayValue}
              readOnly
              className="w-40 text-center bg-transparent focus:outline-none cursor-pointer text-sm md:text-base"
            />
          )}
        />
        <Calendar className="w-5 h-5 text-gray-500 mr-1 text-center" />
      </div>
    </div>
  );
}

export default ModalData;
